
// native
import { Injectable, NgZone } from '@angular/core';
import { firstValueFrom } from 'rxjs';

// service
import { ItemsSyncService } from './items-sync.service';
import { BulkCreateItemService } from './bulk-create.service';
import { CitationsEventService } from './citations-event.service';
import { ToastService } from './toast.service';
import { GlobalsService } from './globals.service';
import { CollectionsService } from './collections.service';
import { MatcherService } from './matcher.service';

// models
import { IDisplayItem, CitationSelection, ShowRemovedPayload, DocumentCitations, ItemsByOriginalId, ICollectionTypeSchema, ICollectionCustomField } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  constructor(
    private itemsSyncService: ItemsSyncService,
    private citationsEventService: CitationsEventService,
    private bulkCreateItemService: BulkCreateItemService,
    private toastService: ToastService,
    private ngZone: NgZone,
    private globalsService: GlobalsService,
    private collectionService: CollectionsService,
    private matcherService: MatcherService
  ) { }

  onCitationSelect(citation: CitationSelection | null) {
    this.citationsEventService.setSelectedCitation(citation);
  }

  async getBibliographyItems(citations: DocumentCitations): Promise<IDisplayItem[] | ShowRemovedPayload> {
    const documentItemsResponse = await this.itemsSyncService.getItemsByDocumentCitations(citations, true);

    const items = documentItemsResponse.items;
    const nullItemsCount = documentItemsResponse.nullItemsCount;

    if (!nullItemsCount) {
      return items;
    }

    const toastPromise = new Promise<IDisplayItem[] | ShowRemovedPayload>((resolve, reject) => {
      const confirmCallback = () => {
        resolve(items);
      };

      const cancelCallback = () => {
        resolve({
          canceledByUser: true,
          items
        });
      };

      // because of GDocs promise
      this.ngZone.run(() => {
        this.toastService.openToast({
          text: `${this.globalsService.isSectionsModeOn ? 'One or more document sections contain' : 'This document contains'} reference${nullItemsCount > 1 ? 's' : ''} to ${nullItemsCount} paper${nullItemsCount > 1 ? 's' : ''} 
                that ${nullItemsCount > 1 ? 'have' : 'has'} been removed from user library. After updating bibliography ${nullItemsCount > 1 ? 'those' : 'that'}
                reference${nullItemsCount > 1 ? 's' : ''} will be removed from the document. If you click "Show removed", ${this.globalsService.isSectionsModeOn ? 'that sections' : ''} bibliography will not 
                be updated, but ${nullItemsCount > 1 ? 'those' : 'that'} reference${nullItemsCount > 1 ? 's' : ''} will be highlighted. 
                Do you want to proceed with updating the bibliography?`,
          type: 'error',
          confirmButton: {
            text: 'OK',
            callback: confirmCallback
          },
          cancelButton: {
            text: 'Show removed',
            callback: cancelCallback
          },
          autoClose: false,
          closeDisabled: true
        });
      });

    });

    return toastPromise;
  }

  async getItemsFromDocumentItems(items: IDisplayItem[]): Promise<{ items: ItemsByOriginalId, addedCount: number, existingCount: number; }> {
    if (this.matcherService.matcherCollectionId && this.collectionService.collections.find(collection => collection.id === this.matcherService.matcherCollectionId))
      return this.getItemsFromDocumentItemsWithMatching(items);

    const originalIds = items.map(item => item.import_data.original_id);
    const originalIdItemMap = await this.itemsSyncService.checkOriginalIds(originalIds);

    const syncedItemsIds: string[] = Object.values(originalIdItemMap).filter(value => !!value).map(value => value.collection_id + ':' + value.id);
    const existingItems = (await this.itemsSyncService.getItemsByDocumentCitations({ ids: syncedItemsIds })).items;

    existingItems.forEach(item => {
      if (item.import_data.original_id)
        originalIdItemMap[item.import_data.original_id] = item;
    });

    const unsyncedItems = items.filter(item => !originalIdItemMap[item.import_data.original_id]);
    const unsyncedOriginalIds = unsyncedItems.map(item => item.import_data.original_id);

    const addedItems = await this.bulkCreateItemService.bulkCreateItems(unsyncedItems, true);

    unsyncedOriginalIds.forEach((id, index) => {
      originalIdItemMap[id] = addedItems[index];
    });

    return Promise.resolve({
      items: originalIdItemMap,
      addedCount: addedItems.filter(item => !item?.merged).length,
      existingCount: existingItems.length
    });
  }

  async getItemsFromDocumentItemsWithMatching(items: IDisplayItem[]): Promise<{ items: ItemsByOriginalId, addedCount: number, existingCount: number; }> {

    const originalIds = items.map(item => item.import_data.original_id);
    const originalIdItemMap = await this.itemsSyncService.checkOriginalIds(originalIds);

    const syncedItemsIds: string[] = Object.values(originalIdItemMap).filter(value => !!value).map(value => value.collection_id + ':' + value.id);
    const existingItems = (await this.itemsSyncService.getItemsByDocumentCitations({ ids: syncedItemsIds })).items;

    existingItems.forEach(item => {
      if (item.import_data.original_id)
        originalIdItemMap[item.import_data.original_id] = item;
    });

    // all items not found by server are considered unmatched and are subject of manual UI matcher
    const unmatchedItems = items.filter(item => !originalIdItemMap[item.import_data.original_id]);

    // if there are no umatched items dont open matcher
    if (!unmatchedItems?.length)
      return Promise.resolve({
        items: originalIdItemMap,
        addedCount: 0,
        existingCount: existingItems.length
      });

    this.matcherService.itemsToMatch$.next(unmatchedItems);

    // get matched items through manual UI matching
    const matchedEndnoteItems = await firstValueFrom(this.matcherService.allItemsIdsMatched$.asObservable());

    const matchedOriginalIds = matchedEndnoteItems.map(item => item.originalId);
    const matchedItemsIds = matchedEndnoteItems.map(item => item.id);

    const matchedItems = (await this.itemsSyncService.getItemsByDocumentCitations({ ids: matchedItemsIds })).items;

    matchedOriginalIds.forEach((originalId, index) => {
      originalIdItemMap[originalId] = matchedItems[index];
    });

    // unmatched items added through bulk create to targeted library if user has write rights, othwerwise it is added to personal library
    const unsyncedItems = items.filter(item => !originalIdItemMap[item.import_data.original_id]);
    const unsyncedOriginalIds = unsyncedItems.map(item => item.import_data.original_id);

    const addedItems = await this.bulkCreateItemService.bulkCreateItems(unsyncedItems, true, this.matcherService.unmatchedTargetLibraryId);

    unsyncedOriginalIds.forEach((id, index) => {
      originalIdItemMap[id] = addedItems[index];
    });

    return Promise.resolve({
      items: originalIdItemMap,
      addedCount: addedItems.filter(item => !item?.merged).length,
      existingCount: existingItems.length + matchedItems.length
    });
  }

  getTypeSchema(collectionId: string): ICollectionTypeSchema {
    return this.collectionService.collections.find(collection => collection.id === collectionId)?.custom_type_schema;
  }

  getCustomFields(collectionId: string): ICollectionCustomField[] {
    return this.collectionService.collections.find(collection => collection.id === collectionId)?.custom_fields;
  }
}